// src/pages/Onboarding.tsx - ENHANCED WITH COMPLETE COLLECTION MODAL

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Plus,
  Clock, 
  AlertTriangle, 
  RefreshCw,
  User,
  Calendar,
  Package,
  CheckCircle,
  Play,
  UserPlus,
  Badge,
  ArrowRight,
  Trash2,
  ChevronDown,
  Shield,
  Shirt,
  Radio,
  Package2,
  Zap,
  Car,
  ArrowLeft,
  CheckCircle2,
  Minus,
  PlusCircle
} from 'lucide-react';
import TabbedSessionManagement from '../components/onboarding/TabbedSessionManagement';
import { onboardingAPI } from '../services/onboardingAPI';
import { userAPI } from '../services/userAPI';
import { OnboardingSession, RoleTemplate } from '../types/onboarding';
import { User as UserType } from '../types/user';
import CompleteCollectionModal from '../components/onboarding/CompleteCollectionModal';

// Fixed types for session summary
interface OnboardingSessionSummary {
  sessionId: string;
  officerName: string;
  badgeNumber: string;
  roleTemplateName: string;
  status: 'Draft' | 'Active' | 'Pending' | 'Ready' | 'Completed';
  progress: {
    categoriesComplete: number;
    categoriesTotal: number;
    itemsPending: number;
    percentComplete: number;
  };
  startDate: string;
  lastUpdated: string;
  createdBy: string;
}

// Fixed status type
type StatusType = 'Draft' | 'Active' | 'Pending' | 'Ready' | 'Completed';

const getStatusColor = (status: StatusType): string => {
  const colors: Record<StatusType, string> = {
    'Draft': 'gray',
    'Active': 'blue', 
    'Pending': 'yellow',
    'Ready': 'green',
    'Completed': 'green'
  };
  return colors[status] || 'gray';
};

const getStatusIcon = (status: StatusType) => {
  switch (status) {
    case 'Draft': return Clock;
    case 'Active': return Play;
    case 'Pending': return AlertTriangle;
    case 'Ready': return CheckCircle;
    case 'Completed': return CheckCircle;
    default: return Clock;
  }
};

// Helper function to convert OnboardingSession to OnboardingSessionSummary
const sessionToSummary = (session: OnboardingSession): OnboardingSessionSummary => {
  return {
    sessionId: session.sessionId,
    officerName: session.officerName,
    badgeNumber: session.badgeNumber,
    roleTemplateName: session.roleTemplateName,
    status: session.status as StatusType,
    progress: {
      categoriesComplete: session.progress?.categoriesComplete || 0,
      categoriesTotal: session.progress?.categoriesTotal || 0,
      itemsPending: session.progress?.itemsPending || 0,
      percentComplete: session.progress?.percentComplete || 0
    },
    startDate: session.startDate,
    lastUpdated: session.updatedAt || session.createdAt || new Date().toISOString(),
    createdBy: session.createdBy || 'Unknown'
  };
};

// Category icon mapping
const getCategoryIcon = (categoryName: string) => {
  const name = categoryName.toLowerCase();
  if (name.includes('firearm') || name.includes('weapon')) return Zap;
  if (name.includes('uniform') || name.includes('shirt') || name.includes('pant')) return Shirt;
  if (name.includes('protective') || name.includes('vest') || name.includes('armor')) return Shield;
  if (name.includes('communication') || name.includes('radio')) return Radio;
  if (name.includes('vehicle') || name.includes('car')) return Car;
  if (name.includes('duty') || name.includes('belt')) return Package2;
  return Package;
};

// 🎯 SESSION INTERFACE COMPONENTS
const SessionHeader: React.FC<{ session: OnboardingSession }> = ({ session }) => (
  <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
    <div className="flex items-center justify-between mb-4">
      <h1 className="text-2xl font-bold text-gray-900">
        Officer {session.officerName} - Equipment Onboarding
      </h1>
      <div className="flex items-center space-x-2">
        <span className={`px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800`}>
          {session.status}
        </span>
      </div>
    </div>
    
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div className="flex items-center space-x-2">
        <Badge className="w-4 h-4 text-gray-500" />
        <span><strong>Badge:</strong> #{session.badgeNumber}</span>
      </div>
      <div className="flex items-center space-x-2">
        <User className="w-4 h-4 text-gray-500" />
        <span><strong>Role:</strong> {session.roleTemplateName}</span>
      </div>
      <div className="flex items-center space-x-2">
        <Calendar className="w-4 h-4 text-gray-500" />
        <span><strong>Start:</strong> {new Date(session.startDate).toLocaleDateString()}</span>
      </div>
      <div className="flex items-center space-x-2">
        <CheckCircle2 className="w-4 h-4 text-gray-500" />
        <span><strong>Progress:</strong> {session.progress?.percentComplete || 0}%</span>
      </div>
    </div>
  </div>
);

// ✅ ENHANCED: Quantity Selection Component for Bulk Items
const QuantitySelector: React.FC<{
  itemId: string;
  maxQuantity: number;
  selectedQuantity: number;
  onQuantityChange: (itemId: string, quantity: number) => void;
  disabled?: boolean;
}> = ({ itemId, maxQuantity, selectedQuantity, onQuantityChange, disabled = false }) => {
  const handleIncrement = () => {
    if (selectedQuantity < maxQuantity) {
      onQuantityChange(itemId, selectedQuantity + 1);
    }
  };

  const handleDecrement = () => {
    if (selectedQuantity > 1) {
      onQuantityChange(itemId, selectedQuantity - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    const clampedValue = Math.max(1, Math.min(value, maxQuantity));
    onQuantityChange(itemId, clampedValue);
  };

  const isAtMax = selectedQuantity >= maxQuantity;
  const isAtMin = selectedQuantity <= 1;

  return (
    <div className="flex items-center space-x-2">
      <span className="text-sm text-gray-700 font-medium">Quantity:</span>
      <div className="flex items-center border border-gray-300 rounded-md">
        <button
          onClick={handleDecrement}
          disabled={disabled || isAtMin}
          className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          <Minus className="w-4 h-4" />
        </button>
        <input
          type="number"
          min="1"
          max={maxQuantity}
          value={selectedQuantity}
          onChange={handleInputChange}
          disabled={disabled}
          className="w-16 px-2 py-1 text-center text-sm border-none focus:outline-none disabled:bg-gray-50"
        />
        <button
          onClick={handleIncrement}
          disabled={disabled || isAtMax}
          className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          <PlusCircle className="w-4 h-4" />
        </button>
      </div>
      <span className="text-xs text-gray-500">of {maxQuantity} available</span>
    </div>
  );
};

// ✅ ENHANCED: Category Accordion Component with Bulk Quantity Support
// ✅ FIXED: Category Accordion Component with stable state management
const CategoryAccordion: React.FC<{
  category: any;
  sessionId: string;
  isExpanded: boolean;
  onToggle: () => void;
  onItemSelect: (item: any, categoryId: string, quantity: number) => void;
}> = ({ category, sessionId, isExpanded, onToggle, onItemSelect }) => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedItems, setHasLoadedItems] = useState(false);
  const [reservingItems, setReservingItems] = useState<Set<string>>(new Set());
  // ✅ FIXED: Store the last sessionId we loaded for, not current
  const [loadedForSession, setLoadedForSession] = useState<string>('');
  // ✅ NEW: Track selected quantities for bulk items
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});

  const CategoryIcon = getCategoryIcon(category.name || category.categoryName);

  // ✅ FIXED: Only reset when we actually need to (session changed AND we had loaded data)
  useEffect(() => {
    if (sessionId && sessionId !== loadedForSession && hasLoadedItems) {
      console.log(`🔄 Session changed from ${loadedForSession} to ${sessionId} - resetting category state`);
      setItems([]);
      setHasLoadedItems(false);
      setLoadedForSession('');
      setReservingItems(new Set());
      setSelectedQuantities({});
    }
  }, [sessionId, loadedForSession, hasLoadedItems]);

  // ✅ FIXED: Load items only when expanded, not loaded for this session, and have valid IDs
  useEffect(() => {
    if (isExpanded && 
        sessionId && 
        category.categoryId && 
        (!hasLoadedItems || loadedForSession !== sessionId)) {
      
      console.log(`📦 Loading items for category: ${category.categoryId} in session: ${sessionId}`);
      loadCategoryItems();
    }
  }, [isExpanded, sessionId, category.categoryId, hasLoadedItems, loadedForSession]);

  const loadCategoryItems = async () => {
    // ✅ PREVENT: Multiple simultaneous loads
    if (loading) {
      console.log(`⚠️ Already loading items for category ${category.categoryId}, skipping`);
      return;
    }
    
    const targetSessionId = sessionId;
    
    if (!targetSessionId || !category.categoryId) {
      console.error('❌ Missing sessionId or categoryId:', { 
        sessionId: targetSessionId, 
        categoryId: category.categoryId 
      });
      return;
    }

    setLoading(true);
    try {
      console.log(`📦 Loading items for category: ${category.categoryId} in session: ${targetSessionId}`);
      const response = await onboardingAPI.getCategoryItems(targetSessionId, category.categoryId);
      
      // ✅ FIXED: Only update state if this is still the current session
      if (targetSessionId !== sessionId) {
        console.log(`⚠️ Session ID changed during loading (${targetSessionId} -> ${sessionId}), ignoring response`);
        return;
      }
      
      if (response.success && response.data) {
        const availableItems = response.data.items || [];
        console.log(`✅ Loaded ${availableItems.length} items for category ${category.categoryId}`);
        
        setItems(availableItems);
        setHasLoadedItems(true);
        setLoadedForSession(targetSessionId); // ✅ TRACK: Which session we loaded for
        
        // ✅ Initialize quantities for bulk items
        const initialQuantities: Record<string, number> = {};
        availableItems.forEach((item: any) => {
          if (item.itemType !== 'SERIALIZED') {
            initialQuantities[item.itemId] = 1;
          }
        });
        setSelectedQuantities(initialQuantities);
      } else {
        console.error('❌ Failed to load category items:', response.error);
        setItems([]);
        setHasLoadedItems(true);
        setLoadedForSession(targetSessionId);
      }
    } catch (error) {
      console.error('❌ Error loading category items:', error);
      setItems([]);
      setHasLoadedItems(true);
      setLoadedForSession(targetSessionId);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle quantity changes for bulk items
  const handleQuantityChange = (itemId: string, quantity: number) => {
    setSelectedQuantities(prev => ({
      ...prev,
      [itemId]: quantity
    }));
  };

  const handleItemSelect = async (item: any) => {
    // ✅ PREVENT: Multiple selections of same item
    if (reservingItems.has(item.itemId)) {
      console.log(`⚠️ Item ${item.itemId} is already being reserved, ignoring click`);
      return;
    }

    try {
      const quantity = item.itemType !== 'SERIALIZED' ? 
        (selectedQuantities[item.itemId] || 1) : 1;
      
      console.log(`🎯 Selecting item: ${item.itemName} (${item.itemId}) quantity: ${quantity}`);
      
      // ✅ TRACK: Item being reserved
      setReservingItems(prev => {
  const newSet = new Set(prev);
  newSet.add(item.itemId);
  return newSet;
});
      
      // ✅ Call parent handler
      await onItemSelect(item, category.categoryId, quantity);
      
      // ✅ OPTIMISTIC UPDATE: Update local state immediately
      if (item.itemType === 'SERIALIZED') {
        // Remove serialized item completely
        setItems(prev => {
          const filtered = prev.filter(i => i.itemId !== item.itemId);
          console.log(`🗑️ Removed serialized item ${item.itemId}, ${filtered.length} items remaining`);
          return filtered;
        });
      } else {
        // For bulk items, decrease available quantity
        setItems(prev => prev.map(i => {
          if (i.itemId === item.itemId) {
            const newAvailable = Math.max(0, (i.availableQuantity || i.quantity || 1) - quantity);
            console.log(`📦 Updated bulk item ${item.itemId}: ${i.availableQuantity} -> ${newAvailable}`);
            return { ...i, availableQuantity: newAvailable };
          }
          return i;
        }).filter(i => (i.availableQuantity || 0) > 0));
        
        // Reset quantity selection
        setSelectedQuantities(prev => ({
          ...prev,
          [item.itemId]: 1
        }));
      }
      
    } catch (error) {
      console.error('❌ Error selecting item:', error);
      // ✅ On error, you might want to refresh the items to get accurate state
    } finally {
      // ✅ ALWAYS: Clear reserving state
      setReservingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.itemId);
        return newSet;
      });
    }
  };

 // ✅ FIXED: For serialized items, check if already selected. For bulk items, always allow if available.
  const isSerializedItemSelected = (itemId: string, itemType: string) => {
    if (itemType !== 'SERIALIZED') {
      return false; // Bulk items are never "selected" - they just have reduced quantity
    }
    return category.selectedItems?.some((selected: any) => selected.itemId === itemId) || false;
  };

  const isItemReserving = (itemId: string) => {
    return reservingItems.has(itemId);
  };

  const handleRefreshItems = () => {
    console.log(`🔄 Force refreshing items for category: ${category.categoryId}`);
    setHasLoadedItems(false);
    setLoadedForSession('');
    setItems([]);
    setSelectedQuantities({});
    // Items will reload automatically due to useEffect
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border mb-4">
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <CategoryIcon className="w-5 h-5 text-blue-600" />
          <span className="font-semibold text-gray-900">{category.name || category.categoryName}</span>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              ({category.quantitySelected || 0}/{category.quantityRequired || 1} selected)
            </span>
            {(category.quantitySelected || 0) >= (category.quantityRequired || 1) && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>
          
          {category.requiresAttestation && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
              Attestation Required
            </span>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
          isExpanded ? 'rotate-180' : ''
        }`} />
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4">
          {/* Show selected items */}
          {category.selectedItems && category.selectedItems.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Selected Items:</h4>
              <div className="space-y-2">
                {category.selectedItems.map((selectedItem: any, index: number) => (
                  <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-green-900">{selectedItem.itemName}</span>
                        {selectedItem.serialNumber && (
                          <span className="text-sm text-green-700 ml-2">
                            Serial: {selectedItem.serialNumber}
                          </span>
                        )}
                        <span className="text-sm text-green-700 ml-2">
                          Qty: {selectedItem.quantity || 1}
                        </span>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading items...</span>
            </div>
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => {
                const isSerialized = item.itemType === 'SERIALIZED';
                const isSelected = isSerializedItemSelected(item.itemId, item.itemType);
                const isReserving = isItemReserving(item.itemId);
                const selectedQty = selectedQuantities[item.itemId] || 1;
                const maxAvailable = item.availableQuantity || item.quantity || 1;
                
                return (
                  <div key={item.itemId} className={`border rounded-lg p-4 transition-colors ${
                    isSelected 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{item.itemName || item.name}</h4>
                      <div className="flex items-center space-x-1">
                        {item.itemType === 'SERIALIZED' ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            Serialized
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                            Bulk
                          </span>
                        )}
                        {isSelected && (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-3">
                      {item.itemType === 'SERIALIZED' ? (
                        <p><strong>Serial:</strong> {item.serialNumber || 'N/A'}</p>
                      ) : (
                        <p><strong>Available:</strong> {maxAvailable}</p>
                      )}
                      {item.manufacturer && (
                        <p><strong>Make:</strong> {item.manufacturer}</p>
                      )}
                      {item.model && (
                        <p><strong>Model:</strong> {item.model}</p>
                      )}
                    </div>

                    {/* Quantity Selector for Bulk Items */}
                    {item.itemType !== 'SERIALIZED' && !isSelected && (
                      <div className="mb-3">
                        <QuantitySelector
                          itemId={item.itemId}
                          maxQuantity={maxAvailable}
                          selectedQuantity={selectedQty}
                          onQuantityChange={handleQuantityChange}
                          disabled={isReserving}
                        />
                      </div>
                    )}

                    <button
                      onClick={() => handleItemSelect(item)}
                      disabled={isSelected || isReserving || (item.itemType !== 'SERIALIZED' && selectedQty > maxAvailable)}
                      className={`w-full px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-center space-x-2 ${
                        isSelected
                          ? 'bg-green-100 text-green-800 cursor-not-allowed'
                          : isReserving
                          ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                          : selectedQty > maxAvailable
                          ? 'bg-red-100 text-red-800 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isReserving ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Reserving...</span>
                        </>
                      ) : isSelected ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span>Reserved</span>
                        </>
                      ) : selectedQty > maxAvailable ? (
                        <span>Insufficient Stock</span>
                      ) : (
                        <span>
                          Reserve {item.itemType !== 'SERIALIZED' ? `${selectedQty}x` : ''} Item{selectedQty > 1 ? 's' : ''}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : hasLoadedItems ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Items Available</h3>
              <p className="text-gray-600 mb-4">No equipment available in this category.</p>
              
              <div className="text-xs text-gray-500 mb-4 space-y-1">
                <p>Session ID: {sessionId}</p>
                <p>Category ID: {category.categoryId}</p>
                <p>Loaded for Session: {loadedForSession}</p>
              </div>
              
              <button
                onClick={handleRefreshItems}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                Refresh Items
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// ✅ ENHANCED: Session Actions with Complete Collection Modal
const SessionActions: React.FC<{
  onBack: () => void;
  session: OnboardingSession;
  onSessionUpdate: (updatedSession: OnboardingSession) => void;
}> = ({ onBack, session, onSessionUpdate }) => {
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const hasReservedItems = session.reservedItems && session.reservedItems.length > 0;
  const canCompleteCollection = hasReservedItems;

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border p-6 mt-6">
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Management</span>
          </button>

          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              Progress: {session.progress?.categoriesComplete || 0} of {session.progress?.categoriesTotal || 0} categories
              {hasReservedItems && (
                <span className="ml-2 text-blue-600">
                  • {session.reservedItems.length} items reserved
                </span>
              )}
            </div>
            
            <button
              onClick={() => setShowCompleteModal(true)}
              disabled={!canCompleteCollection}
              className={`flex items-center space-x-2 px-6 py-2 rounded-md transition-colors ${
                canCompleteCollection
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title={!canCompleteCollection ? 'No items reserved for collection' : 'Complete equipment collection'}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Complete Collection</span>
            </button>
          </div>
        </div>
        
        {!canCompleteCollection && (
          <div className="mt-3 text-sm text-gray-500">
            Select equipment items first before completing the collection process.
          </div>
        )}
      </div>

      {/* Complete Collection Modal */}
      <CompleteCollectionModal
        session={session}
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        onComplete={(updatedSession) => {
          setShowCompleteModal(false);
          onSessionUpdate(updatedSession);
        }}
      />
    </>
  );
};

// 🎯 MAIN COMPONENT
const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId?: string }>();
  
  // Management interface state
  const [activeSessions, setActiveSessions] = useState<OnboardingSessionSummary[]>([]);
  const [officers, setOfficers] = useState<UserType[]>([]);
  const [roleTemplates, setRoleTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Session interface state
  const [session, setSession] = useState<OnboardingSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  
  // Session deletion state
  const [sessionToDelete, setSessionToDelete] = useState<{sessionId: string, officerName: string} | null>(null);
  const [deletingSession, setDeletingSession] = useState(false);
  
  // Quick start form
  const [quickStart, setQuickStart] = useState({
    officerId: '',
    roleTemplateId: '',
    startDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [creatingSession, setCreatingSession] = useState(false);

  // Load management data
  useEffect(() => {
    if (!sessionId) {
      loadOnboardingData();
    }
  }, [sessionId]);

  // Load session data
  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    }
  }, [sessionId]);

  const loadOnboardingData = async () => {
    setLoading(true);
    try {
      const [officersResponse, templatesResponse] = await Promise.all([
        userAPI.getUsers(1, 1000, { employmentStatus: ['Active'] }),
        onboardingAPI.getRoleTemplates().catch(() => ({ success: false, data: [] }))
      ]);

      const activeOfficers = officersResponse.users.filter(user => user.employmentStatus === 'Active');
      setOfficers(activeOfficers);

      if (templatesResponse.success && templatesResponse.data) {
        setRoleTemplates(templatesResponse.data);
      }

      try {
        const sessionsResponse = await onboardingAPI.getActiveSessions();
        if (sessionsResponse.success && sessionsResponse.data) {
          const sessions = sessionsResponse.data.sessions || [];
          const sessionSummaries = sessions.map(sessionToSummary);
          setActiveSessions(sessionSummaries);
        }
      } catch (err) {
        console.log('Could not load active sessions:', err);
        setActiveSessions([]);
      }

    } catch (err) {
      console.error('Error loading onboarding data:', err);
      setError('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (sessionId: string) => {
    setSessionLoading(true);
    try {
      console.log(`🔍 Loading session: ${sessionId}`);
      const response = await onboardingAPI.getSession(sessionId);
      
      if (response.success && response.data) {
        const sessionData = response.data.session;
        setSession(sessionData);
        
        // Expand first category by default
        if (sessionData.categories && sessionData.categories.length > 0) {
          setExpandedCategories(new Set([sessionData.categories[0].categoryId]));
        }
        
        console.log(`✅ Session loaded successfully`);
      } else {
        throw new Error(response.error?.message || 'Failed to load session');
      }
    } catch (err) {
      console.error('❌ Error loading session:', err);
      setError(err instanceof Error ? err.message : 'Failed to load session');
    } finally {
      setSessionLoading(false);
    }
  };

  const handleQuickStart = async (quickStartData: any) => {
  setCreatingSession(true);
  setError(null);

  try {
    const response = await onboardingAPI.createSession({
      officerId: quickStartData.officerId,
      roleTemplateId: quickStartData.roleTemplateId,
      startDate: quickStartData.startDate,
      notes: quickStartData.notes
    });

    if (response.success && response.data) {
      const session = response.data.session;
      navigate(`/onboarding/${session.sessionId}`);
    } else {
      throw new Error(response.error?.message || 'Failed to create session');
    }
  } catch (err) {
    console.error('Error creating session:', err);
    setError(err instanceof Error ? err.message : 'Failed to create session');
  } finally {
    setCreatingSession(false);
  }
};

  const handleResumeSession = (sessionId: string) => {
    navigate(`/onboarding/${sessionId}`);
  };

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;

    setDeletingSession(true);
    try {
      const response = await onboardingAPI.deleteSession(sessionToDelete.sessionId);
      
      if (response.success) {
        setActiveSessions(prev => prev.filter(s => s.sessionId !== sessionToDelete.sessionId));
        setSessionToDelete(null);
        setError(`Session for ${sessionToDelete.officerName} deleted successfully`);
        setTimeout(() => setError(null), 3000);
      } else {
        throw new Error(response.error?.message || 'Failed to delete session');
      }
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete session');
    } finally {
      setDeletingSession(false);
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // 🔧 CRITICAL FIX: Enhanced handleItemSelect with proper state merging
const handleItemSelect = async (item: any, categoryId: string, quantity: number = 1) => {
  if (!session) return;

  try {
    // 🔍 DEBUG: Log current session state before API call
    console.log('📊 BEFORE API CALL - Session state:', {
      sessionId: session.sessionId,
      officerName: session.officerName,
      badgeNumber: session.badgeNumber,
      roleTemplateName: session.roleTemplateName,
      status: session.status,
      categoriesCount: session.categories?.length || 0,
      reservedItemsCount: session.reservedItems?.length || 0
    });
    
    console.log(`🎯 Selecting item for session ${session.sessionId}:`, {
      itemName: item.itemName || item.name,
      itemId: item.itemId,
      categoryId: categoryId,
      quantity: quantity
    });
    
    const response = await onboardingAPI.selectItem(session.sessionId, {
      categoryId: categoryId,
      itemId: item.itemId,
      quantity: quantity,
      notes: `Selected during onboarding session - ${quantity}x ${item.itemName || item.name}`
    });

    if (response.success && response.data) {
      // 🔍 DEBUG: Log API response structure
      console.log('📡 API RESPONSE STRUCTURE:', {
        hasSession: !!response.data.session,
        hasDirectData: !!response.data.officerName,
        responseKeys: Object.keys(response.data),
        sessionKeys: response.data.session ? Object.keys(response.data.session) : 'no session key'
      });
      
      // 🔧 CRITICAL FIX: Intelligent state merging based on API response structure
      let updatedSession: OnboardingSession;
      
      if (response.data.session) {
        // API returned { session: {...} } structure
        console.log('📦 API Response contains session object');
        updatedSession = {
          ...session,  // Preserve existing session data
          ...response.data.session,  // Merge updated fields
          // Ensure critical fields are preserved if not in API response
          officerName: response.data.session.officerName || session.officerName,
          badgeNumber: response.data.session.badgeNumber || session.badgeNumber,
          roleTemplateName: response.data.session.roleTemplateName || session.roleTemplateName,
          startDate: response.data.session.startDate || session.startDate,
          sessionId: session.sessionId, // Never let this change
        };
      } else if (response.data.officerName) {
        // API returned session data directly
        console.log('📦 API Response is direct session data');
        updatedSession = {
          ...session,  // Preserve existing session data
          ...response.data,  // Merge updated fields
          sessionId: session.sessionId, // Never let this change
        };
      } else {
        // Fallback: minimal update, preserve everything critical
        console.log('⚠️ API Response structure unknown, using minimal update');
        updatedSession = {
          ...session,
          // Only update specific fields we know should change
          categories: response.data.categories || session.categories,
          reservedItems: response.data.reservedItems || session.reservedItems,
          progress: response.data.progress || session.progress,
          totalValue: response.data.totalValue || session.totalValue,
          updatedAt: new Date().toISOString()
        };
      }
      
      // 🔍 DEBUG: Log final merged state
      console.log('📊 AFTER MERGE - Final session state:', {
        sessionId: updatedSession.sessionId,
        officerName: updatedSession.officerName,
        badgeNumber: updatedSession.badgeNumber,
        roleTemplateName: updatedSession.roleTemplateName,
        status: updatedSession.status,
        categoriesCount: updatedSession.categories?.length || 0,
        reservedItemsCount: updatedSession.reservedItems?.length || 0,
        hasAllCriticalFields: !!(updatedSession.officerName && updatedSession.badgeNumber && updatedSession.roleTemplateName)
      });
      
      // 🔧 CRITICAL FIX: Set the properly merged session
      setSession(updatedSession);
      
      // Show success message
      const itemName = item.itemName || item.name;
      const message = quantity > 1 ? 
        `${quantity}x ${itemName} reserved successfully for Officer ${session.officerName}` :
        `${itemName} reserved successfully for Officer ${session.officerName}`;
      
      setError(message);
      setTimeout(() => setError(null), 3000);
      
      console.log(`✅ Item reserved successfully, session updated without state reset`);
    } else {
      throw new Error(response.error?.message || 'Failed to reserve item');
    }
    
  } catch (err) {
    console.error('❌ Error selecting item:', err);
    setError(err instanceof Error ? err.message : 'Failed to select item');
    setTimeout(() => setError(null), 5000);
  }
};
  // ✅ NEW: Handle session updates from completion modal
  const handleSessionUpdate = (updatedSession: OnboardingSession) => {
    setSession(updatedSession);
    setError(`Collection completed! Officer ${updatedSession.officerName} is ready to receive equipment.`);
    setTimeout(() => setError(null), 5000);
  };
      
  const handleBackToManagement = () => {
    navigate('/onboarding');
  };

  // Show loading for management interface
  if (loading && !sessionId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-lg text-gray-700">Loading onboarding data...</span>
        </div>
      </div>
    );
  }

  // 🎯 SESSION INTERFACE - Enhanced with Complete Collection Modal
  if (sessionId) {
    if (sessionLoading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            <span className="text-lg text-gray-700">Loading session...</span>
          </div>
        </div>
      );
    }

    if (!session) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md text-center">
            <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Session Not Found</h2>
            <p className="text-gray-700 mb-6">
              Could not load session: {sessionId}
            </p>
            <button
              onClick={handleBackToManagement}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700"
            >
              Back to Management
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-800">{error}</span>
              </div>
            </div>
          )}

          {/* Session Header */}
          <SessionHeader session={session} />

          {/* Category Accordion */}
          {session.categories && session.categories.length > 0 ? (
            session.categories.map((category) => (
              <CategoryAccordion
                key={category.categoryId}
                category={category}
                sessionId={session.sessionId}
                isExpanded={expandedCategories.has(category.categoryId)}
                onToggle={() => handleCategoryToggle(category.categoryId)}
                onItemSelect={handleItemSelect}
              />
            ))
          ) : (
            <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Categories Found</h3>
              <p className="text-gray-600">This session does not have any equipment categories configured.</p>
            </div>
          )}

          {/* Session Actions */}
          <SessionActions
            session={session}
            onBack={handleBackToManagement}
            onSessionUpdate={handleSessionUpdate}
          />
        </div>
      </div>
    );
  }

  // 🎯 MANAGEMENT INTERFACE - Using TabbedSessionManagement
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Onboarding Management</h1>
          <p className="mt-2 text-gray-600">Manage officer equipment onboarding sessions</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-red-800">{error}</span>
              <button 
                onClick={() => setError(null)} 
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* ✅ NEW: Tabbed Session Management */}
        <TabbedSessionManagement
          officers={officers}
          roleTemplates={roleTemplates}
          onQuickStart={handleQuickStart}
          creatingSession={creatingSession}
        />
      </div>
    </div>
  );
};

export default Onboarding;